import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Button, Tooltip } from '@vd/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeftRight, Check, Copy, Eye, FileText, Flag, History, Maximize, MousePointerClick, PenLine, Terminal, Wifi,
} from 'lucide-react';
import type { Followed, SessionTrail, Ticket } from '@vd/types';
import { api } from '../../lib/api';
import { formatClock, formatFull, formatStamp, formatWhen } from '../../lib/time';
import { buildTicketReproduction, buildTimeline, type TimelineEntry } from './sessionTimeline';
import { SessionCards } from './SessionCards';
import { clickMarksFor } from './clickMarks';
import styles from './SessionWindow.module.css';

/** The DevTools tab a page's summary opens on. */
export type SessionSourceTab = 'console' | 'network' | 'errors';

/** One page of the visit: where, for how long, what the person did, and what the page counted. */
interface PageVisit {
  /** The page entry; null for what happened before the first page the trail saw. */
  page: TimelineEntry | null;
  /** How long until the next page, or the report; null when neither is known. */
  stayedMs: number | null;
  actions: TimelineEntry[];
  devtools: { errors: number; failedRequests: number; requests: number; consoleErrors: number; warnings: number };
}

/**
 * The Session tab of the debugging window (Jason, 22 Oct; one window with
 * DevTools since 24 Oct): the visit a ticket came from, told the way the
 * person lived it. Their setup first; then each page they were on, how long
 * they stayed, and what they did there; then the report. What the page
 * logged, requested and threw is counted under each page, not listed: the
 * DevTools tab has the detail, one click away.
 */
export function SessionView({ ticket, onOpenTab, teamReporter }: {
  ticket: Ticket;
  onOpenTab: (tab: SessionSourceTab) => void;
  /** The teammate who filed it, by name, when it is a teammate's own report (not a visitor's). */
  teamReporter?: string | null;
}) {
  const timeline = useMemo(() => buildTimeline(ticket), [ticket]);
  // Each click numbered on the screenshot, by its action (TOADO-919).
  const markOf = useMemo(() => new Map((clickMarksFor(ticket)?.marks ?? []).map((mark) => [mark.actionIndex, mark.n])), [ticket]);
  const report = timeline.entries.find((entry) => entry.kind === 'report') ?? null;
  const visits = useMemo(() => visitsOf(timeline.entries, report, ticket), [timeline, report, ticket]);
  const untimedRequests = timeline.untimed.length;

  /**
   * A timeline mark pressed: down to its row in the list and lit for two
   * seconds (Jason, 23 Oct). A mark with no row of its own, such as a failed
   * request, goes to the page it happened on.
   */
  const listRef = useRef<HTMLOListElement>(null);
  const pick = (entry: TimelineEntry) => {
    const list = listRef.current;
    if (!list) return;
    const at = entry.at ?? 0;
    const onPage = [...visits].reverse().find((visit) => visit.page && (visit.page.at ?? 0) <= at)?.page;
    const target = list.querySelector<HTMLElement>(`[data-entry="${entry.kind === 'report' ? 'report' : `${entry.kind}-${entry.index}`}"]`)
      ?? (onPage ? list.querySelector<HTMLElement>(`[data-entry="page-${onPage.index}"]`) : null)
      ?? list.querySelector<HTMLElement>('[data-entry]');
    if (!target) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
    target.setAttribute('data-picked', '');
    window.setTimeout(() => target.removeAttribute('data-picked'), 2000);
  };
  const devtoolsTotal = visits.reduce((sum, visit) => sum + visit.devtools.errors + visit.devtools.requests + visit.devtools.consoleErrors + visit.devtools.warnings, 0) + untimedRequests;

  return (
    <div className={styles.window} data-testid="session-window">
      <div className={styles.topBar}>
        <Summary ticket={ticket} visits={visits} report={report} teamReporter={teamReporter} />
      </div>
      <SessionCards ticket={ticket} className={styles.cards} />
      <ActivityStrip entries={timeline.entries} onPick={pick} />
      <ol className={styles.visits} data-testid="session-timeline" ref={listRef}>
        {visits.map((visit, index) => (
          <li key={visit.page ? `page-${visit.page.index}` : `before-${index}`} className={styles.visit} data-testid="session-page" data-entry={visit.page ? `page-${visit.page.index}` : 'before'} id={rowId(ticket.id, visit.page ? `page-${visit.page.index}` : 'before')}>
            <div className={styles.pageHead}>
              <FileText size={15} aria-hidden />
              <div className={styles.pageTitle}>
                <span className={styles.pageLabel}>{visit.page ? visit.page.label.replace(/^opened /, '') : 'Before the first page we saw'}</span>
                {visit.stayedMs !== null && <span className={styles.stayed}>{duration(visit.stayedMs)} on this page</span>}
              </div>
              {visit.page && <EntryTime entry={visit.page} report={report} />}
            </div>
            {visit.actions.length > 0 ? (
              <ol className={styles.actions}>
                {visit.actions.map((entry) => <ActionRow key={`${entry.kind}-${entry.index}`} entry={entry} report={report} mark={markOf.get(entry.index)} ticketId={ticket.id} />)}
              </ol>
            ) : (
              <p className={styles.nothing}>No clicks or changes were recorded on this page.</p>
            )}
            <PageDevtools counts={visit.devtools} onOpenTab={onOpenTab} />
          </li>
        ))}
        {report && (
          <li className={styles.reportRow} data-testid="session-report" data-entry="report" id={rowId(ticket.id, 'report')}>
            <Flag size={15} aria-hidden />
            <span>Reported</span>
            <EntryTime entry={report} report={null} />
          </li>
        )}
      </ol>
      {untimedRequests > 0 && (
        <p className={styles.untimed}>
          {count(untimedRequests, 'request')} with no time to place on a page.{' '}
          <button type="button" className={styles.linkButton} onClick={() => onOpenTab('network')}>See them in DevTools</button>
        </p>
      )}
      {devtoolsTotal === 0 && (
        <p className={styles.untimed}>Nothing was logged, requested or thrown that this ticket kept.</p>
      )}
    </div>
  );
}

/**
 * The visit as pages, in order. A ticket with no recorded pages (an older
 * extension capture) is one visit to the page it was captured on, with what
 * the page logged counted under it.
 */
function visitsOf(entries: TimelineEntry[], report: TimelineEntry | null, ticket: Ticket): PageVisit[] {
  const empty = (page: TimelineEntry | null): PageVisit => ({ page, stayedMs: null, actions: [], devtools: { errors: 0, failedRequests: 0, requests: 0, consoleErrors: 0, warnings: 0 } });
  const visits: PageVisit[] = [];
  for (const entry of entries) {
    if (entry.kind === 'report') continue;
    if (entry.kind === 'page') { visits.push(empty(entry)); continue; }
    if (visits.length === 0) visits.push(empty(null));
    const visit = visits[visits.length - 1]!;
    if (entry.kind === 'action') visit.actions.push(entry);
    else if (entry.kind === 'error') visit.devtools.errors += 1;
    else if (entry.kind === 'request') {
      visit.devtools.requests += 1;
      if (entry.failed) visit.devtools.failedRequests += 1;
    } else if (entry.kind === 'console') {
      if (entry.failed) visit.devtools.consoleErrors += 1;
      else if (entry.detail === 'warn') visit.devtools.warnings += 1;
    }
  }
  if (visits.length === 0) {
    const at = report?.at ?? null;
    visits.push(empty(ticket.sourceUrl ? { at, kind: 'page', label: pageOf(ticket.sourceUrl), failed: false, index: -1 } : null));
  } else if (visits[0]!.page === null && !visits.some((visit) => visit.page) && ticket.sourceUrl) {
    visits[0]!.page = { at: null, kind: 'page', label: pageOf(ticket.sourceUrl), failed: false, index: -1 };
  }
  visits.forEach((visit, index) => {
    const from = visit.page?.at;
    const next = visits[index + 1]?.page?.at ?? report?.at ?? null;
    visit.stayedMs = typeof from === 'number' && typeof next === 'number' && next >= from ? next - from : null;
  });
  return visits;
}

function pageOf(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

/** "Reported Oct 22, 2:52 PM after 4 min on the site", with the counts that matter to a person. */
function Summary({ ticket, visits, report, teamReporter }: { ticket: Ticket; visits: PageVisit[]; report: TimelineEntry | null; teamReporter?: string | null }) {
  const trail = ticket.capture?.session as Partial<SessionTrail> | undefined;
  const clicks = Array.isArray(trail?.actions) ? trail.actions.filter((action) => action?.kind === 'click').length : 0;
  const rage = Array.isArray(trail?.actions) ? trail.actions.filter((action) => action?.kind === 'click' && action.rage === true).length : 0;
  const pages = visits.filter((visit) => visit.page && visit.page.index >= 0).length;
  const errors = visits.reduce((sum, visit) => sum + visit.devtools.errors, 0);
  const first = visits.find((visit) => typeof visit.page?.at === 'number')?.page?.at ?? null;
  const span = first !== null && report?.at != null ? report.at - first : null;
  const facts: Array<{ text: string; tone?: 'danger' | 'warning' }> = [
    { text: count(pages, 'page') },
    { text: count(clicks, 'click') },
    { text: count(rage, 'rage click'), tone: 'warning' as const },
    { text: count(errors, 'error'), tone: 'danger' as const },
  ].filter((fact) => fact.text);
  const reportedAt = report?.at ?? ticket.createdAt;
  // Who, where the report says (Jason, 25 Oct: it had a card of its own).
  // Given by the person and not checked, as the ticket's header also says.
  const name = ticket.reporterName?.trim() || null;
  const email = ticket.reporterEmail?.trim() || null;
  // A teammate's own report names no reporter: it is theirs (board ticket t--4ugrP).
  const teammate = !name && !email ? teamReporter?.trim() || null : null;
  return (
    <div className={styles.summary}>
      <div className={styles.reported}>
        <Flag size={14} aria-hidden />
        <span>
          Reported
          {(name || email) && (
            <>
              {' by '}
              <span className={styles.reporter} title="Given by the person who reported it, and not verified">{name ?? email}</span>
              {name && email && <> (<a className={styles.reporterEmail} href={`mailto:${email}`}>{email}</a>)</>}
              {' on'}
            </>
          )}
          {teammate && <>{' by '}<span className={styles.reporter}>{teammate}</span>, a team member, on</>}{' '}
          <time dateTime={new Date(reportedAt).toISOString()} title={formatFull(reportedAt)}>
            {formatWhen(reportedAt, { exact: true })}
          </time>
          {span !== null && span >= 1000 && <> after {duration(span)} on the site</>}
        </span>
      </div>
      {facts.length > 0 && (
        <ul className={styles.facts}>
          {facts.map((fact) => <li key={fact.text} data-tone={fact.tone}>{fact.text}</li>)}
        </ul>
      )}
      {Array.isArray(trail?.earlierReports) && trail.earlierReports.length > 0 && (
        <EarlierReportsLine ticket={ticket} named={trail.earlierReports.length} reportedAt={reportedAt} />
      )}
    </div>
  );
}

/**
 * The person's earlier reports from the same visit (a widget remembers them
 * in the tab), as links to the tickets they became, where this person can
 * open them (TOADO-948's lookup, `GET /tickets/:id/timeline`). Said as a
 * line under the summary, and drawn only when the trail names any, so a
 * ticket without them asks for nothing.
 */
function EarlierReportsLine({ ticket, named, reportedAt }: { ticket: Ticket; named: number; reportedAt: number | string }) {
  const visit = useQuery({
    queryKey: ['ticket-visit', ticket.id],
    queryFn: () => api.getTicketVisit(ticket.id),
    staleTime: 60_000,
  });
  const found = visit.data?.context?.earlierReports;
  const tickets = found?.tickets ?? [];
  const at = typeof reportedAt === 'number' ? reportedAt : Date.parse(reportedAt);
  if (tickets.length > 0) {
    const hidden = Math.max(0, (found?.count ?? named) - tickets.length);
    return (
      <p className={styles.earlier} data-testid="session-earlier-reports">
        <History size={13} aria-hidden />
        <span>{tickets.length === 1 && hidden === 0 ? 'They also reported, earlier in this visit:' : 'Their earlier reports from this visit:'}</span>
        {tickets.map((earlier) => <EarlierTicketLink key={earlier.id} id={earlier.id} before={at - Date.parse(earlier.createdAt)} />)}
        {hidden > 0 && <span>and {hidden} more you cannot open here</span>}
      </p>
    );
  }
  const one = named === 1;
  const reports = one ? 'An earlier report from this visit' : `${named} earlier reports from this visit`;
  const text = visit.isPending
    ? `${reports}.`
    : found?.unresolved
      ? `${reports} could not be looked up just now.`
      : `${reports} ${one ? 'is not a ticket' : 'are not tickets'} you can open here: ${one ? 'it may be' : 'they may be'} in another project, or older than 30 days.`;
  return (
    <p className={styles.earlier} data-testid="session-earlier-reports">
      <History size={13} aria-hidden />
      <span>{text}</span>
    </p>
  );
}

/** An earlier report as a link to its ticket, by its title once read (the ticket page's own query), its id until then. */
function EarlierTicketLink({ id, before }: { id: string; before: number }) {
  const earlier = useQuery({ queryKey: ['ticket', id], queryFn: () => api.getTicket(id), staleTime: 60_000 });
  const title = earlier.data?.title?.trim();
  return (
    <Link to={`/tickets/${id}`} className={styles.earlierLink} title={title ? `${title} (${id})` : id}>
      <span className={styles.earlierTitle}>{title || id}</span>
      {Number.isFinite(before) && before >= 1000 && <span className={styles.earlierWhen}> {duration(before)} before</span>}
    </Link>
  );
}

/** What the page counted, in a line, and the way to the detail. */
function PageDevtools({ counts, onOpenTab }: { counts: PageVisit['devtools']; onOpenTab: (tab: SessionSourceTab) => void }) {
  const parts: Array<{ text: string; tab: SessionSourceTab; tone?: 'danger' | 'warning' }> = [
    { text: count(counts.errors, 'error'), tab: 'errors' as const, tone: 'danger' as const },
    { text: count(counts.failedRequests, 'failed request'), tab: 'network' as const, tone: 'danger' as const },
    { text: count(counts.consoleErrors, 'console error'), tab: 'console' as const, tone: 'danger' as const },
    { text: count(counts.warnings, 'warning'), tab: 'console' as const, tone: 'warning' as const },
    { text: count(counts.requests - counts.failedRequests, 'request'), tab: 'network' as const },
  ].filter((part) => part.text);
  if (parts.length === 0) return null;
  return (
    <div className={styles.devtools} data-testid="session-page-devtools">
      {parts.map((part) => (
        <button key={part.text} type="button" className={styles.devtoolsPart} data-tone={part.tone} onClick={() => onOpenTab(part.tab)} title="See in DevTools">
          {part.tab === 'errors' ? <AlertTriangle size={12} aria-hidden /> : part.tab === 'network' ? <ArrowLeftRight size={12} aria-hidden /> : <Terminal size={12} aria-hidden />}
          {part.text}
        </button>
      ))}
    </div>
  );
}

/**
 * The visit drawn across time, first thing to the report: a mark for each
 * page, click and error, so a burst of errors or a run of rage clicks shows
 * before anyone reads a row. Decoration: every mark is in the pages below.
 */
function ActivityStrip({ entries, onPick }: { entries: TimelineEntry[]; onPick: (entry: TimelineEntry) => void }) {
  const timed = entries.filter((entry) => entry.at !== null && (entry.kind === 'page' || entry.kind === 'action' || entry.kind === 'report' || entry.failed));
  if (timed.length < 2) return null;
  const start = timed[0]!.at!;
  const end = timed[timed.length - 1]!.at!;
  const width = Math.max(end - start, 1);
  const first = timed[0]!;
  const last = timed[timed.length - 1]!;
  // The key counts only what is drawn, so a visit with nothing between its
  // ends still reads as a timeline rather than an empty bar.
  const key = [
    { kind: 'page', text: count(timed.filter((entry) => entry.kind === 'page').length, 'page') },
    { kind: 'action', text: count(timed.filter((entry) => entry.kind === 'action').length, 'action') },
    { kind: 'failed', text: count(timed.filter((entry) => entry.failed).length, 'error') },
  ].filter((item) => item.text);
  return (
    <div className={styles.strip} data-testid="session-strip">
      <div className={styles.stripHead}>
        <span className={styles.stripTitle}>Timeline</span>
        <ul className={styles.stripKey}>
          {key.map((item) => <li key={item.kind} data-key={item.kind}>{item.text}</li>)}
        </ul>
      </div>
      <div className={styles.stripTrack}>
        {timed.map((entry) => (
          // The slot holds the place on the axis; the tooltip measures the mark inside it.
          <span key={`${entry.kind}-${entry.index}`} className={styles.slot} style={{ left: `${((entry.at! - start) / width) * 100}%` }}>
            <Tooltip content={<MarkTip entry={entry} />} delay={80} multiline>
              {/* Takes you to its row in the list below (Jason, 23 Oct). */}
              <button
                type="button"
                className={styles.mark}
                data-kind={entry.kind}
                data-failed={entry.failed || undefined}
                data-flag={entry.flag ? true : undefined}
                aria-label={markLabel(entry)}
                onClick={() => onPick(entry)}
              >
                {entry.kind === 'report' && <Flag size={10} />}
              </button>
            </Tooltip>
          </span>
        ))}
      </div>
      <div className={styles.stripEnds}>
        <span>{first.kind === 'page' ? 'Opened' : 'From'} {formatStamp(start)}</span>
        <span className={styles.stripSpan}>{duration(end - start)} on the site</span>
        <span>{last.kind === 'report' ? 'Reported' : 'To'} {formatStamp(end)}</span>
      </div>
    </div>
  );
}

/** A mark's name for a screen reader: what it is, and when. */
function markLabel(entry: TimelineEntry): string {
  const clock = formatClock(entry.at!, { ms: false });
  return entry.kind === 'report' ? `Reported ${clock}` : `${entry.label}${entry.flag ? ` (${entry.flag})` : ''}, ${clock}`;
}

/** What a mark on the timeline is, and when. */
function MarkTip({ entry }: { entry: TimelineEntry }) {
  const clock = formatClock(entry.at!, { ms: false });
  if (entry.kind === 'report') return <><strong>Reported {clock}</strong><br />When they sent this ticket.</>;
  const what = entry.label.charAt(0).toUpperCase() + entry.label.slice(1);
  return <><strong>{clock}</strong><br />{what}{entry.flag && ` (${entry.flag})`}</>;
}

function actionIcon(entry: TimelineEntry): ReactElement {
  if (entry.label.startsWith('clicked') || entry.label.startsWith('switched to')) return <MousePointerClick size={13} />;
  if (entry.label.startsWith('changed')) return <PenLine size={13} />;
  if (entry.label.startsWith('window')) return <Maximize size={13} />;
  if (entry.label.includes('online') || entry.label.includes('offline')) return <Wifi size={13} />;
  return <Eye size={13} />;
}

/**
 * A row's own id (t-M_Mect): rows look alike to the pin locator (the same
 * classes, often the same text), so a pin on one could not be found again and
 * was held by its spot on the page. An id unique to the row lets it be.
 */
function rowId(ticketId: string, entry: string): string {
  return `session-${ticketId}-${entry}`;
}

function ActionRow({ entry, report, mark, ticketId }: { entry: TimelineEntry; report: TimelineEntry | null; mark?: number; ticketId: string }) {
  return (
    <li className={styles.action} data-kind={entry.kind} data-flag={entry.flag ? true : undefined} data-entry={`${entry.kind}-${entry.index}`} id={rowId(ticketId, `${entry.kind}-${entry.index}`)}>
      <span className={styles.node} aria-hidden>{actionIcon(entry)}</span>
      <span className={styles.label}>
        {/* The number this click has on the screenshot (TOADO-919). */}
        {mark !== undefined && <span className={styles.clickMark} data-testid="click-mark-number" aria-label={`Click ${mark} on the screenshot`}>{mark}</span>}
        {entry.label}
        {entry.flag && <span className={styles.flag}>{entry.flag}</span>}
        {entry.locator && <Locator locator={entry.locator} />}
        {entry.followed && <FollowedLine followed={entry.followed} />}
      </span>
      <EntryTime entry={entry} report={report} />
    </li>
  );
}

/**
 * What the page did in the seconds after an action (TOADO-918), counted: "then 2 requests, 1 error, 2 failed".
 * Counted only, as the page's own lines are: what the page logged and requested is in DevTools, a button away.
 */
function FollowedLine({ followed }: { followed: Followed }) {
  const parts = [count(followed.requests, 'request'), count(followed.errors, 'error'), count(followed.console, 'console line')].filter(Boolean).join(', ');
  return (
    <span className={styles.followed} data-testid="followed">
      then {parts}
      {followed.failed > 0 && <>, <span className={styles.followedFailed}>{followed.failed} failed</span></>}
    </span>
  );
}

/**
 * Copy reproduction (TOADO-926): the Playwright test of the steps the person took, as
 * `GET /tickets/:id/reproduction` and MCP's `get_reproduction` give it. Shown when the person
 * did something on a page, not for a visit that is only pages.
 */
/**
 * Copy reproduction: a Playwright test of the steps the person took, as a
 * button in the debugging window's header beside its tabs (Jason, 24 Oct).
 * Nothing when the visit has no action to reproduce, only pages.
 */
export function CopyReproduction({ ticket }: { ticket: Ticket }) {
  const reproduction = useMemo(() => buildTicketReproduction(ticket), [ticket]);
  const acted = useMemo(() => buildTimeline(ticket).entries.some((entry) => entry.kind === 'action'), [ticket]);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const gaps = reproduction.gaps;
  const copy = () => {
    void navigator.clipboard?.writeText(reproduction.playwright).then(() => setCopied(true), () => undefined);
  };
  if (!acted) return null;
  return (
    <Tooltip content={`A Playwright test of the ${count(reproduction.steps.length, 'step')} the person took${gaps > 0 ? `, with ${count(gaps, 'step')} to finish by hand` : ''}`}>
      <Button variant="secondary" size="sm" leftIcon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copy} data-testid="copy-reproduction">
        {copied ? 'Copied' : 'Copy reproduction'}
      </Button>
    </Tooltip>
  );
}

/** A Playwright locator for the element, from its role and name, to find it again (TOADO-917). */
function Locator({ locator }: { locator: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    void navigator.clipboard?.writeText(locator).then(() => setCopied(true), () => undefined);
  };
  return (
    <span className={styles.locator} data-testid="locator">
      <code className={styles.locatorCode}>{locator}</code>
      <button type="button" className={styles.locatorCopy} onClick={copy} aria-label={copied ? 'Locator copied' : 'Copy locator'}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </span>
  );
}

/** The clock time, and how long before the report it came. */
function EntryTime({ entry, report }: { entry: TimelineEntry; report: TimelineEntry | null }) {
  if (entry.at === null) return <span className={styles.time} />;
  const before = report && entry.kind !== 'report' ? report.at! - entry.at : null;
  return (
    <span className={styles.time}>
      <time dateTime={new Date(entry.at).toISOString()} title={`${formatFull(entry.at)}, ${formatClock(entry.at)}`}>{formatClock(entry.at, { ms: false })}</time>
      {before !== null && <span className={styles.before}>{before >= 0 ? `${duration(before)} before` : `${duration(-before)} after`}</span>}
    </span>
  );
}

function count(n: number, noun: string): string {
  return n === 0 ? '' : `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** "0.4 s", "12 s", "4 min 2 s", "1 h 5 min". */
export function duration(ms: number): string {
  if (ms < 10_000) return `${(Math.max(ms, 0) / 1000).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return seconds % 60 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}
